import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
} from 'react-native';
import { usePaymentsContext } from '../providers/PaymentsProvider';
import { Subscription, SubscriptionStatus } from '../../shared/types';
import { formatCurrency, formatDate } from '../../shared/utils/formatting';

export interface NativeSubscriptionViewProps {
  subscription?: Subscription;
  onCancel?: (subscriptionId: string) => void;
  onReactivate?: (subscriptionId: string) => void;
  onUpdate?: (subscriptionId: string) => void;
  style?: any;
}

export const NativeSubscriptionView: React.FC<NativeSubscriptionViewProps> = ({
  subscription,
  onCancel,
  onReactivate,
  onUpdate,
  style,
}) => {
  const { activeSubscription, loading, error } = usePaymentsContext();
  const [currentSubscription, setCurrentSubscription] =
    useState<Subscription | null>(null);

  useEffect(() => {
    setCurrentSubscription(subscription || activeSubscription);
  }, [subscription, activeSubscription]);

  const handleCancel = () => {
    if (!currentSubscription) return;

    Alert.alert(
      'Cancel Subscription',
      'Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.',
      [
        { text: 'Keep Subscription', style: 'cancel' },
        {
          text: 'Cancel',
          style: 'destructive',
          onPress: () => onCancel?.(currentSubscription.id),
        },
      ]
    );
  };

  const handleReactivate = () => {
    if (!currentSubscription) return;

    Alert.alert(
      'Reactivate Subscription',
      'Would you like to reactivate your subscription?',
      [
        { text: 'No', style: 'cancel' },
        {
          text: 'Reactivate',
          onPress: () => onReactivate?.(currentSubscription.id),
        },
      ]
    );
  };

  const getStatusColor = (status: SubscriptionStatus): string => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return '#10B981';
      case SubscriptionStatus.TRIALING:
        return '#3B82F6';
      case SubscriptionStatus.PAST_DUE:
        return '#F59E0B';
      case SubscriptionStatus.CANCELED:
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  const getStatusText = (status: SubscriptionStatus): string => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return 'Active';
      case SubscriptionStatus.TRIALING:
        return 'Trial';
      case SubscriptionStatus.PAST_DUE:
        return 'Past Due';
      case SubscriptionStatus.CANCELED:
        return 'Canceled';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, style]}>
        <Text style={styles.loadingText}>Loading subscription...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={[styles.container, style]}>
        <Text style={styles.errorText}>Error: {error.message}</Text>
      </View>
    );
  }

  if (!currentSubscription) {
    return (
      <View style={[styles.container, style]}>
        <Text style={styles.noSubscriptionText}>No active subscription</Text>
      </View>
    );
  }

  const isActive =
    currentSubscription.status === SubscriptionStatus.ACTIVE ||
    currentSubscription.status === SubscriptionStatus.TRIALING;
  const isCanceled = currentSubscription.status === SubscriptionStatus.CANCELED;

  return (
    <ScrollView style={[styles.container, style]}>
      <View style={styles.header}>
        <Text style={styles.title}>Subscription Details</Text>
        <View
          style={[
            styles.statusBadge,
            { backgroundColor: getStatusColor(currentSubscription.status) },
          ]}
        >
          <Text style={styles.statusText}>
            {getStatusText(currentSubscription.status)}
          </Text>
        </View>
      </View>

      <View style={styles.detailsContainer}>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Amount:</Text>
          <Text style={styles.detailValue}>
            {formatCurrency(
              currentSubscription.amount || 0,
              currentSubscription.currency
            )}
          </Text>
        </View>

        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Billing Interval:</Text>
          <Text style={styles.detailValue}>{currentSubscription.interval}</Text>
        </View>

        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Current Period:</Text>
          <Text style={styles.detailValue}>
            {formatDate(currentSubscription.currentPeriodStart)} -{' '}
            {formatDate(currentSubscription.currentPeriodEnd)}
          </Text>
        </View>

        {currentSubscription.trialEnd && (
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Trial Ends:</Text>
            <Text style={styles.detailValue}>
              {formatDate(currentSubscription.trialEnd)}
            </Text>
          </View>
        )}

        {currentSubscription.cancelAtPeriodEnd && (
          <View style={styles.warningContainer}>
            <Text style={styles.warningText}>
              Your subscription will cancel on{' '}
              {formatDate(currentSubscription.currentPeriodEnd)}
            </Text>
          </View>
        )}
      </View>

      <View style={styles.actionsContainer}>
        {isActive && !currentSubscription.cancelAtPeriodEnd && (
          <>
            <TouchableOpacity
              style={styles.primaryButton}
              onPress={() => onUpdate?.(currentSubscription.id)}
            >
              <Text style={styles.primaryButtonText}>Update Plan</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.dangerButton}
              onPress={handleCancel}
            >
              <Text style={styles.dangerButtonText}>Cancel Subscription</Text>
            </TouchableOpacity>
          </>
        )}

        {currentSubscription.cancelAtPeriodEnd && (
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={handleReactivate}
          >
            <Text style={styles.primaryButtonText}>
              Reactivate Subscription
            </Text>
          </TouchableOpacity>
        )}

        {isCanceled && (
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={() => onUpdate?.(currentSubscription.id)}
          >
            <Text style={styles.primaryButtonText}>Subscribe Again</Text>
          </TouchableOpacity>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  detailsContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  detailLabel: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  detailValue: {
    fontSize: 14,
    color: '#1F2937',
    fontWeight: '600',
  },
  warningContainer: {
    backgroundColor: '#FEF3C7',
    borderRadius: 8,
    padding: 12,
    marginTop: 16,
  },
  warningText: {
    color: '#92400E',
    fontSize: 14,
    textAlign: 'center',
  },
  actionsContainer: {
    gap: 12,
  },
  primaryButton: {
    backgroundColor: '#3B82F6',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  dangerButton: {
    backgroundColor: '#EF4444',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  dangerButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  loadingText: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: 16,
  },
  errorText: {
    textAlign: 'center',
    color: '#EF4444',
    fontSize: 16,
  },
  noSubscriptionText: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: 16,
  },
});

export default NativeSubscriptionView;
